import { type ReactNode } from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { type CellHeaderStyle } from '../CellHeader';
import { Card, MinimalButton } from '../SharedStyles';

export type ResultType = 'chart' | 'table' | 'error';

export type MagicElementState = 'default' | 'active' | 'loading';

export interface DemoProps {
  height?: number;
  hideHeader?: boolean;
  onComplete?: () => void;
}

export const MAGIC_ELEMENT_SIZES: Record<string, number> = {
  Default: 30,
  StillThinking: 94
};

export type CursorPosition = { anchor: CursorAnchor; x: number; y: number };

export const MOUSE_CURSOR_LOCATIONS: Record<
  string,
  Record<string, CursorPosition>
> = {
  SQL: {
    MagicElement: { x: -2, y: 70, anchor: 'right' },
    PopoverGenerate: { x: 175, y: 136, anchor: 'right' },
    PopoverEdit: { x: 175, y: 186, anchor: 'right' },
    PopoverDebug: { x: 175, y: 236, anchor: 'right' },
    PopoverExplain: { x: 175, y: 286, anchor: 'right' }
  },
  Python: {
    MagicElement: { x: -2, y: 30, anchor: 'right' },
    PopoverGenerate: { x: 175, y: 104, anchor: 'right' },
    PopoverEdit: { x: 175, y: 154, anchor: 'right' },
    PopoverDebug: { x: 175, y: 204, anchor: 'right' },
    PopoverExplain: { x: 175, y: 254, anchor: 'right' }
  }
};

export const DEMO_FIXED_HEIGHT = 320;

export type MagicEditBarMode = 'generate' | 'edit';

export const pause = (_: number | undefined) =>
  new Promise(resolve => setTimeout(resolve, _));

export interface DefaultCellProps {
  commentPopupOverride?: boolean;
  customResult?: ReactNode;
  defaultResultType?: ResultType;
  demoConfig?: CellDemoConfig;
  fixedHeight?: number;
  headerStyle?: CellHeaderStyle;
  hideHeader?: boolean;
  hideOutputToggle?: boolean;
  showCommentCount?: boolean;
  showLineNumbers?: boolean;
  showResult?: boolean;
  showTypeAhead?: boolean;
  tableRowCount?: number;
}

export type CellDemoConfig = {
  code?: ReactNode;
  errorOutput?: {
    description: string;
    title: string;
  };
  isMagicBarOpen: boolean;
  isMagicEnabled: boolean;
  isMagicLoading: boolean;
  isMagicPopoverOpen: boolean;
  magicEditBarMode: MagicEditBarMode;
  magicPrompt?: string;
  resultType?: ResultType;
};

export const DemoWrapper = styled.div<{ $blurring?: boolean }>`
  flex: 1 1 auto;
  display: flex;
  min-width: 0;
  position: relative;
  width: 100%;
  pointer-events: none;

  transition:
    filter ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};

  ${({ $blurring }) =>
    $blurring &&
    css`
      filter: blur(6px);
      opacity: 0.96;
    `}
`;

export const DEFAULT_CELL_DEMO_CONFIG: CellDemoConfig = {
  code: null,
  isMagicBarOpen: false,
  isMagicEnabled: false,
  isMagicPopoverOpen: false,
  isMagicLoading: false,
  magicPrompt: undefined,
  magicEditBarMode: 'edit',
  resultType: 'table'
};

export const Top = styled.div`
  display: flex;
  align-items: stretch;
  padding: 3px;
`;

export const Inner = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const Code = styled.div<{ $showLineNumbers?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 12px 0;
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-weight: 400;
  font-size: 12px;
  line-height: 19px;
  position: relative;

  ${({ $showLineNumbers }) =>
    $showLineNumbers &&
    css`
      padding-left: 25px;
    `}
`;

export const Line = styled.div<{ $indent?: boolean }>`
  position: relative;
  padding: 0 15px;
  height: 19px;
  line-height: 19px
  overflow: hidden;
  white-space: nowrap;
  ${({ $indent }) => $indent && `padding-left: 40px;`}
`;

export const LineNumbers = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 15px;
  opacity: 0.4;
  overflow: hidden;
  bottom: 15px;

  span {
    font-family: ${({ theme }) => theme.fontFamily.MONO};
    font-weight: 500;
    font-size: 11px;
    line-height: 16px;
  }
`;

export const Blue = styled.span`
  color: #147eb3;
`;

export const Pink = styled.span`
  color: #db2c6f;
`;

export const Teal = styled.span`
  color: #00a396;
`;

export const Green = styled.span`
  color: #00a35c;
`;

export const Gray = styled.span`
  color: #717a94;
`;

export const Placeholder = styled.span`
  color: ${({ theme }) => theme.appUI.fontColor.MUTED};
  cursor: pointer;

  span {
    box-shadow: inset 0 -1px 0 0 ${({ theme }) => theme.appUI.borderColor.DEFAULT};
  }
`;

export const Adding = styled.div`
  position: relative;
  width: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.12)},
    ${({ theme }) => rgba(theme.appUI.magic.primaryColor, 0.07)}
  );
`;

export const Removing = styled.div`
  position: relative;
  width: 100%;
  background: linear-gradient(
    90deg,
    ${({ theme }) => rgba(theme.appUI.magic.removingColor, 0.12)},
    ${({ theme }) => rgba(theme.appUI.magic.removingColor, 0.07)}
  );
`;

export const Typeahead = styled(Card)`
  position: absolute;
  z-index: 9;
  left: calc(100% + 2px);
  top: 0;
  border: 1px solid ${({ theme }) => theme.appUI.borderColor.DEFAULT};
  padding: 3px;

  > div:first-child {
    color: #365ca3;
    background-color: ${rgba('#365ca3', 0.1)};
  }
`;

export const TAOption = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  padding: 2px 3px;
  border-radius: 1px;
`;

export const Result = styled.div`
  border-top: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
`;

export const ResultHeader = styled.div`
  display: flex;
  width: 100%;
  flex: 1 1 auto;
  justify-content: center;
  border-bottom: 1px solid ${({ theme }) => theme.appUI.borderColor.MUTED};
  padding: 4px 0;
  gap: 3px;
  font-size: ${({ theme }) => theme.fontSize.SMALL};
`;

export const ResultContent = styled.div`
  display: flex;
`;

export const StyledMinimalButton = styled(MinimalButton).attrs({
  $small: true
})``;

export const Wrapper = styled(Card)`
  width: 100%;
  padding: 0;
  z-index: 3;
  position: relative;
`;

export type CursorStyle = 'default' | 'pointer';
export type CursorAnchor = 'left' | 'right';

export const MouseCursor = styled.div<{
  $anchor: CursorAnchor;
  $style: CursorStyle;
  $visible: boolean;
  $xPos: number;
  $yPos: number;
}>`
  display: flex;
  justify-content: ${({ $style }) =>
    $style === 'default' ? 'flex-start' : 'flex-end'};
  width: 30px;
  height: 30px;
  overflow: hidden;
  position: absolute;
  z-index: 999;
  top: ${({ $yPos }) => `${$yPos}px`};
  left: ${({ $anchor, $xPos }) => ($anchor === 'left' ? `${$xPos}px` : 'auto')};
  right: ${({ $anchor, $xPos }) =>
    $anchor === 'right' ? `${$xPos}px` : 'auto'};
  pointer-events: none;
  opacity: ${({ $visible }) => (!$visible ? '0' : '1')};

  svg {
    flex: none;
  }

  transition:
    top ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    left ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    right ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
`;

export const HexButton = styled.div`
  flex: none;
  display: inline-flex;
  align-items: center;
  border-radius: 2px;
  font-size: 14px;
  gap: 5px;
  justify-content: center;
  padding: 5px 10px;
  height: 30px;
  box-shadow:
    rgb(25 27 52 / 15%) 0px 0px 0px 1px inset,
    rgb(1 1 27 / 2%) 0px 2px 4px 0px,
    rgb(11 14 22 / 2%) 0px 1px 2px 0px;

  color: rgb(1, 1, 27);
  background-color: rgb(255, 255, 255);

  transition: background-color 0.15s cubic-bezier(0.25, 0.1, 0.25, 1) 0s;
  cursor: pointer;

  svg {
    color: ${({ theme }) => theme.appUI.fontColor.MUTED};
  }

  span {
    color: ${({ theme }) => theme.appUI.fontColor.MUTED};
    font-size: 10px;
    padding-top: 2px;
  }
`;
